import { useRef, useState } from "react";
import { View } from "react-native";

import {
  LAST_STOP,
  type PriceRange,
  rangeAfterMove,
  rangePositions,
  thumbText,
} from "@/lib/filters/priceRange";
import type { PriceRangeFilterProps } from "./PriceRangeFilter.types";
import { PriceRangeFields, PriceRangeHeading } from "./PriceRangeParts";
import "./priceRange.web.css";

export { PRICE_STOPS, type PriceRange, priceRangeLabel } from "@/lib/filters/priceRange";

/**
 * `PriceRangeFilter` on the web: the same heading, track and fields as the native variant, with
 * the two thumbs drawn by two native `<input type="range">` laid over one track. A browser already
 * makes those keyboard-operable (arrows step a stop, Home and End jump to the ends) and announces
 * them as sliders with their values, which the native `PanResponder` thumbs could not be on the web.
 *
 * The inputs step through the same dollar stops (`lib/filters/priceRange`). A pointer drag shows
 * its place as it moves and commits on release, like the native drag, so a screen that queries the
 * server sends one request per drag; any other change (a key press, a screen reader's step)
 * commits at once.
 */
export function PriceRangeFilter(
  { value, onChange, label = "Price", testIDPrefix }: PriceRangeFilterProps,
) {
  const [draft, setDraft] = useState<{ lo: number; hi: number; } | null>(null);
  const dragging = useRef(false);
  // The inputs sit on whole stops; two typed amounts between the same stops still get two places.
  const exact = rangePositions(value);
  const at = { lo: Math.round(exact.lo), hi: 0 };
  at.hi = Math.max(Math.round(exact.hi), at.lo + 1);
  const lo = draft?.lo ?? at.lo;
  const hi = draft?.hi ?? at.hi;
  const shown: PriceRange = draft ? rangeAfterMove(value, at, draft) : value;
  const commit = (nextLo: number, nextHi: number) => {
    setDraft(null);
    onChange(rangeAfterMove(value, at, { lo: nextLo, hi: nextHi }));
  };
  const thumb = (end: "min" | "max") => {
    const own = end === "min" ? lo : hi;
    const place = (n: number) => (end === "min" ? { lo: n, hi } : { lo, hi: n });
    // The thumbs never cross: each stops one stop short of the other.
    const clamp = (n: number) => (end === "min" ? Math.min(n, hi - 1) : Math.max(n, lo + 1));
    const text = thumbText(shown, end);
    return (
      <input
        type="range"
        className="price-range-thumb"
        min={0}
        max={LAST_STOP}
        step={1}
        value={own}
        aria-label={end === "min" ? "Minimum price" : "Maximum price"}
        aria-valuetext={text}
        data-testid={`price-range-${end}-thumb`}
        onPointerDown={() => {
          dragging.current = true;
        }}
        // A drag shows its place as it moves and commits on release; any other change (a key, a
        // screen reader's step) commits at once.
        onChange={(e) => {
          const next = place(clamp(Number(e.currentTarget.value)));
          if (dragging.current) setDraft(next);
          else commit(next.lo, next.hi);
        }}
        onPointerUp={(e) => {
          dragging.current = false;
          const next = place(clamp(Number(e.currentTarget.value)));
          commit(next.lo, next.hi);
        }}
      />
    );
  };

  return (
    <View className="gap-3">
      <PriceRangeHeading label={label} range={shown} />
      <div
        className="price-range"
        style={{
          ["--lo" as string]: `${(lo / LAST_STOP) * 100}%`,
          ["--hi" as string]: `${(hi / LAST_STOP) * 100}%`,
        }}
      >
        {thumb("min")}
        {thumb("max")}
      </div>
      <PriceRangeFields value={value} onChange={onChange} testIDPrefix={testIDPrefix} />
    </View>
  );
}
